<template>
  <section
    ref="collectionBrowser"
    class="collection-browser index-ignore"
    @collection-target="activateTarget($event.detail)"
    :data-collection-transition-axis="transitionId ? gridCursorAxes[transitionId] : null"
    :class="[
      `is-${mode}`,
      transitionAction ? `is-transition-${transitionAction}` : '',
      {
        'is-layout-transitioning': Boolean(transitionId),
        'is-archive-open': archiveExpanded
      }
    ]"
  >
    <header v-if="curationActive" class="collection-curation-head">
      <h2>{{ featuredLabel }}</h2>
    </header>
    <div v-if="mode === 'grid'" class="collection-grid">
      <div class="collection-grid-row">
        <template v-for="(entry, entryIndex) in orderedEntries" :key="entry.id">
        <div v-if="curationActive && entryIndex === archiveStartIndex" class="collection-archive-gateway">
          <button
            type="button"
            class="collection-archive-toggle"
            :aria-expanded="archiveExpanded.toString()"
            @click="toggleArchive"
          >
            <span class="collection-archive-copy">
              <strong>{{ archiveLabel }}</strong>
              <span>{{ archiveCountLabel }}</span>
            </span>
            <span class="collection-archive-glyph" :class="{ 'is-open': archiveExpanded }" aria-hidden="true"><SiteIcon name="arrow-down" /></span>
          </button>
        </div>
        <Transition name="collection-archive-card">
        <article
          v-show="isEntryVisible(entry)"
          :id="entry.anchor || entry.id"
          class="collection-card"
          :class="{
            selected: gridLayoutId === entry.id,
            'is-archive-entry': isArchiveEntry(entry),
            'is-detail-navigable': isDetailNavigable(entry),
            'is-signature': isFeatureEntry(entry)
          }"
          :style="{
            viewTransitionName: transitionId === entry.id
              ? 'collection-active-card'
              : `collection-card-${entry.id}`,
            '--archive-delay': `${archivePosition(entry) * 28}ms`
          }"
          @click.self="!isFeatureEntry(entry) && desktopHover && toggle(entry.id)"
        >
            <SignatureProject v-if="entry.showcase" :entry="entry" />
            <template v-else>
            <button
              type="button"
              class="collection-card-summary"
              data-press-intent="custom"
              :class="{ 'is-pressed': pressedId === entry.id }"
              :aria-expanded="isEntryOpen(entry.id).toString()"
              :data-collection-toggle="isEntryOpen(entry.id) ? 'collapse' : 'expand'"
              :data-collection-axis="gridCursorAxes[entry.id]"
              @pointerdown="startPress(entry.id, $event)"
              @pointerup="finishPress(entry.id)"
              @pointercancel="cancelPress(entry.id)"
              @click="toggle(entry.id)"
            >
              <span class="collection-card-meta">
                <span>{{ entry.category }}</span>
                <span>{{ entry.period }}</span>
              </span>
              <strong>{{ entry.title }}</strong>
              <span v-if="entry.subtitle && entry.kind !== 'project'" class="collection-card-subtitle">{{ entry.subtitle }}</span>
              <span class="collection-card-preview">{{ entry.preview }}</span>
              <span class="collection-card-cue" aria-hidden="true">
                {{ gridLayoutId === entry.id ? 'Close' : 'Read' }}
                <span>{{ gridLayoutId === entry.id ? '−' : '+' }}</span>
              </span>
            </button>
            <div
              class="collection-detail-shell"
              :class="{ open: isEntryOpen(entry.id) }"
              :aria-hidden="(!isEntryOpen(entry.id)).toString()"
              :inert="isEntryOpen(entry.id) ? null : ''"
            >
              <div class="collection-detail-clip">
                <div
                  class="collection-detail collection-detail-embedded"
                  :class="{ 'collection-detail-navigable': isDetailNavigable(entry) }"
                  :role="isDetailNavigable(entry) ? 'link' : null"
                  :tabindex="isDetailNavigable(entry) ? 0 : null"
                  :aria-label="isDetailNavigable(entry) ? `Open ${entry.title}` : null"
                  @click="openDetail(entry, $event)"
                  @keydown.enter="openDetail(entry, $event)"
                >
                  <CollectionDetail :entry="entry" embedded @close="close(entry.id)" />
                </div>
              </div>
            </div>
            <button
              v-if="transitionId === entry.id"
              type="button"
              class="collection-transition-hit-area"
              :aria-label="transitionAction === 'collapse' ? `Close ${entry.title}` : `Open ${entry.title}`"
              :data-collection-toggle="transitionAction"
              :data-collection-axis="gridCursorAxes[entry.id]"
              @click.stop="toggle(entry.id)"
            ></button>
            </template>
        </article>
        </Transition>
        </template>
      </div>
    </div>

    <div v-else class="collection-index">
      <template v-for="(entry, entryIndex) in entries" :key="entry.id">
      <div v-if="curationActive && entryIndex === featuredCount" class="collection-archive-gateway">
        <button
          type="button"
          class="collection-archive-toggle"
          :aria-expanded="archiveExpanded.toString()"
          @click="toggleArchive"
        >
          <span class="collection-archive-copy">
            <strong>{{ archiveLabel }}</strong>
            <span>{{ archiveCountLabel }}</span>
          </span>
          <span class="collection-archive-glyph" :class="{ 'is-open': archiveExpanded }" aria-hidden="true"><SiteIcon name="arrow-down" /></span>
        </button>
      </div>
      <Transition name="collection-archive-card">
      <section
        v-show="isEntryVisible(entry)"
        :id="entry.anchor || entry.id"
        class="collection-index-unit"
        :class="{
          'is-archive-entry': isArchiveEntry(entry),
          'is-detail-navigable': isDetailNavigable(entry)
        }"
        :style="{ '--archive-delay': `${archivePosition(entry) * 28}ms` }"
        @click.self="desktopHover && toggle(entry.id)"
      >
        <button
          type="button"
          class="collection-index-row"
          data-press-intent="custom"
          :class="{ selected: isEntryOpen(entry.id), 'is-pressed': pressedId === entry.id }"
          :aria-expanded="isEntryOpen(entry.id).toString()"
          :data-collection-toggle="isEntryOpen(entry.id) ? 'collapse' : 'expand'"
          @pointerdown="startPress(entry.id, $event)"
          @pointerup="finishPress(entry.id)"
          @pointercancel="cancelPress(entry.id)"
          @click="toggle(entry.id)"
        >
          <span class="collection-index-category">{{ entry.category }}</span>
          <strong>{{ entry.title }}</strong>
          <span class="collection-index-period">{{ entry.period }}</span>
          <span class="collection-index-cue" aria-hidden="true">{{ isEntryOpen(entry.id) ? '−' : '+' }}</span>
        </button>
        <div
          class="collection-detail-shell"
          :class="{ open: isEntryOpen(entry.id) }"
          :aria-hidden="(!isEntryOpen(entry.id)).toString()"
          :inert="isEntryOpen(entry.id) ? null : ''"
        >
          <div class="collection-detail-clip">
            <div
              class="collection-detail collection-detail-embedded collection-index-detail"
              :class="{ 'collection-detail-navigable': isDetailNavigable(entry) }"
              :role="isDetailNavigable(entry) ? 'link' : null"
              :tabindex="isDetailNavigable(entry) ? 0 : null"
              :aria-label="isDetailNavigable(entry) ? `Open ${entry.title}` : null"
              @click="openDetail(entry, $event)"
              @keydown.enter="openDetail(entry, $event)"
            >
              <CollectionDetail :entry="entry" embedded show-subtitle @close="close(entry.id)" />
            </div>
          </div>
        </div>
      </section>
      </Transition>
      </template>
    </div>
  </section>
</template>

<script>
import { watchDesktopHover } from '../../utils/desktopHover';
import CollectionDetail from './CollectionDetail.vue';
import SignatureProject from './SignatureProject.vue';
import { acquireInteractiveCursorLock } from '../../utils/interactiveCursorLock';
import { collectionDetailRoute, shouldNavigateCollectionDetail } from '../../utils/collectionDetailNavigation';
import { ensureCollectionCursorStyles } from '../../utils/collectionCursorStyles';
import { watchPreviewCollectionCursor } from '../../utils/previewCollectionCursor';

const GRID_CURSOR_SETTLE_MS = 420;

export default {
  name: 'CollectionBrowser',
  components: { CollectionDetail, SignatureProject },
  emits: ['target-consumed'],
  props: {
    entries: {
      type: Array,
      required: true
    },
    mode: {
      type: String,
      required: true
    },
    targetId: {
      type: String,
      default: ''
    },
    initialOpenId: {
      type: String,
      default: ''
    },
    featuredCount: {
      type: Number,
      default: 0
    },
    featuredLabel: {
      type: String,
      default: 'Selected'
    },
    archiveLabel: {
      type: String,
      default: 'All'
    },
    searchActive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const hasInitialEntry = this.entries.some((entry) => entry.id === this.initialOpenId);
    const initialOpenId = hasInitialEntry ? this.initialOpenId : '';
    const initialOpenIndex = this.entries.findIndex((entry) => entry.id === initialOpenId);
    const archiveInitiallyExpanded = initialOpenIndex >= this.featuredCount && initialOpenIndex >= 0;
    return {
      desktopHover: false,
      openId: initialOpenId,
      indexOpenIds: initialOpenId ? [initialOpenId] : [],
      layoutId: this.mode === 'grid' ? initialOpenId : '',
      transitionId: '',
      transitionAction: '',
      pressedId: '',
      archiveExpanded: archiveInitiallyExpanded,
      archiveVisible: archiveInitiallyExpanded
    };
  },
  computed: {
    curationActive() {
      return !this.searchActive
        && this.featuredCount > 0
        && this.entries.length > this.featuredCount;
    },
    featuredEntries() {
      return this.curationActive ? this.entries.slice(0, this.featuredCount) : this.entries;
    },
    archiveEntries() {
      return this.curationActive ? this.entries.slice(this.featuredCount) : [];
    },
    archiveIds() {
      return new Set(this.archiveEntries.map((entry) => entry.id));
    },
    archiveCountLabel() {
      return `${this.archiveEntries.length} more`;
    },
    archiveStartIndex() {
      return this.orderedEntries.findIndex((entry) => this.isArchiveEntry(entry));
    },
    gridCursorAxes() {
      const axes = {};
      let column = 0;
      let archive = false;
      this.orderedEntries.filter((entry) => this.isEntryVisible(entry)).forEach((entry) => {
        const nextArchive = this.isArchiveEntry(entry);
        if (nextArchive !== archive) column = 0;
        archive = nextArchive;
        if (this.isFeatureEntry(entry)) {
          column = 0;
        } else if (entry.id === this.gridLayoutId) {
          // The full-width card returns to its original column on collapse.
          let originalColumn = 0;
          let originalArchive = false;
          for (const item of this.entries) {
            if (!this.isEntryVisible(item)) continue;
            const nextArchive = this.isArchiveEntry(item);
            if (nextArchive !== originalArchive) originalColumn = 0;
            originalArchive = nextArchive;
            if (item.id === entry.id) break;
            originalColumn = this.isFeatureEntry(item) ? 0 : (originalColumn + 1) % 2;
          }
          axes[entry.id] = originalColumn ? 'right' : 'left';
          column = 0;
        } else {
          axes[entry.id] = column % 2 ? 'right' : 'left';
          column += 1;
        }
      });
      return axes;
    },
    gridLayoutId() {
      const id = this.openId || this.layoutId;
      return this.mode === 'grid' && !this.isFeatureEntry(this.entries.find(entry => entry.id === id)) ? id : '';
    },
    orderedEntries() {
      if (!this.gridLayoutId) return this.entries;
      const selectedIndex = this.entries.findIndex((entry) => entry.id === this.gridLayoutId);
      if (selectedIndex < 0) return this.entries;

      // Signature projects occupy their own row, including filtered results.
      let rowStart = 0;
      let column = 0;
      let archive = false;
      for (let index = 0; index <= selectedIndex; index += 1) {
        const entry = this.entries[index];
        const nextArchive = this.isArchiveEntry(entry);
        if (nextArchive !== archive) column = 0;
        archive = nextArchive;
        if (this.isFeatureEntry(entry) || column === 0) rowStart = index;
        column = this.isFeatureEntry(entry) ? 0 : (column + 1) % 2;
      }
      const before = this.entries.slice(0, rowStart);
      const fromRow = this.entries.slice(rowStart);
      const selected = this.entries[selectedIndex];
      return [...before, selected, ...fromRow.filter((entry) => entry.id !== this.gridLayoutId)];
    }
  },
  watch: {
    mode(nextMode) {
      window.clearTimeout(this._layoutTimer);
      window.clearTimeout(this._activationTimer);
      this.pressedId = '';
      this.transitionId = '';
      this.transitionAction = '';
      if (nextMode === 'index') {
        if (this.openId && !this.indexOpenIds.includes(this.openId)) {
          this.indexOpenIds = [...this.indexOpenIds, this.openId];
        }
        this.layoutId = '';
        return;
      }
      const nextGridId = this.openId || this.indexOpenIds[this.indexOpenIds.length - 1] || '';
      this.openId = nextGridId;
      this.layoutId = nextGridId;
    },
    entries() {
      const validIds = new Set(this.entries.map((entry) => entry.id));
      this.indexOpenIds = this.indexOpenIds.filter((id) => validIds.has(id));
      if (this.openId && !validIds.has(this.openId)) {
        this.openId = this.indexOpenIds[this.indexOpenIds.length - 1] || '';
      }
      if (this.layoutId && !validIds.has(this.layoutId)) this.layoutId = '';
    },
    targetId: {
      immediate: true,
      handler(id) {
        if (id) this.activateTarget(id);
      }
    }
  },
  mounted() {
    ensureCollectionCursorStyles();
    this._stopPreviewCursor = watchPreviewCollectionCursor(this.$refs.collectionBrowser);
    this._stopHover = watchDesktopHover(value => { this.desktopHover = value; });
  },
  unmounted() {
    this._stopPreviewCursor?.();
    this._stopHover?.();
    window.clearTimeout(this._layoutTimer);
    window.clearTimeout(this._pressIntentTimer);
    window.clearTimeout(this._pressTimer);
    window.clearTimeout(this._activationTimer);
    window.clearTimeout(this._gridReverseTimer);
    window.clearTimeout(this._archiveCleanupTimer);
    window.clearTimeout(this._archiveAnimationTimer);
    if (this._archiveAnimationFrame) cancelAnimationFrame(this._archiveAnimationFrame);
    if (this._gridCursorTimers) {
      this._gridCursorTimers.forEach((timer) => window.clearTimeout(timer));
      this._gridCursorTimers.clear();
    }
    if (this._gridCursorReleases) {
      this._gridCursorReleases.forEach((release) => release());
      this._gridCursorReleases.clear();
    }
    if (this._indexAnchorFrame) cancelAnimationFrame(this._indexAnchorFrame);
    if (this._gridTransition && this._gridTransition.skipTransition) {
      this._gridTransition.skipTransition();
    }
  },
  methods: {
    isEntryOpen(id) {
      return this.mode === 'index'
        ? this.indexOpenIds.includes(id)
        : this.openId === id;
    },
    isArchiveEntry(entry) {
      return this.curationActive && this.archiveIds.has(entry.id);
    },
    isEntryVisible(entry) {
      return !this.isArchiveEntry(entry) || this.archiveVisible;
    },
    archivePosition(entry) {
      const index = this.archiveEntries.findIndex((item) => item.id === entry.id);
      return Math.max(0, index);
    },
    clearArchiveOpenState() {
      if (this.mode === 'grid' && this.archiveIds.has(this.openId)) {
        this.openId = '';
        this.layoutId = '';
        this.transitionId = '';
        this.transitionAction = '';
      }
      if (this.mode === 'index') {
        this.indexOpenIds = this.indexOpenIds.filter((id) => !this.archiveIds.has(id));
        this.openId = this.indexOpenIds[this.indexOpenIds.length - 1] || '';
      }
    },
    archiveSpanHeight() {
      const browser = this.$refs.collectionBrowser;
      if (!browser) return 0;
      const entries = Array.from(browser.querySelectorAll('.is-archive-entry'))
        .filter((element) => element.getClientRects().length > 0);
      if (!entries.length) return 0;
      const rects = entries.map((element) => element.getBoundingClientRect());
      return Math.max(...rects.map((rect) => rect.bottom))
        - Math.min(...rects.map((rect) => rect.top));
    },
    clearArchiveAnimationStyles(browser) {
      if (!browser) return;
      browser.style.height = '';
      browser.style.overflow = '';
      browser.style.transition = '';
      browser.style.willChange = '';
    },
    finishArchiveAnimation(browser, token, opening) {
      if (this._archiveAnimationToken !== token) return;
      if (!opening && !this.archiveExpanded) this.clearArchiveOpenState();
      this.$nextTick(() => {
        if (this._archiveAnimationToken !== token) return;
        this.clearArchiveAnimationStyles(browser);
      });
    },
    async toggleArchive() {
      const browser = this.$refs.collectionBrowser;
      const opening = !this.archiveExpanded;
      window.clearTimeout(this._archiveAnimationTimer);
      if (this._archiveAnimationFrame) cancelAnimationFrame(this._archiveAnimationFrame);

      if (!browser || this.prefersReducedMotion()) {
        this.archiveExpanded = opening;
        this.archiveVisible = opening;
        if (!opening) this.clearArchiveOpenState();
        return;
      }

      const token = (this._archiveAnimationToken || 0) + 1;
      this._archiveAnimationToken = token;
      const startHeight = browser.getBoundingClientRect().height;
      browser.style.transition = 'none';
      browser.style.height = `${startHeight}px`;
      browser.style.overflow = 'clip';
      browser.style.willChange = 'height';
      void browser.offsetHeight;

      if (opening) {
        this.archiveExpanded = true;
        this.archiveVisible = true;
        await this.$nextTick();
      } else {
        const archiveHeight = this.archiveSpanHeight();
        this.archiveExpanded = false;
        this.archiveVisible = false;
        await this.$nextTick();
        this.runArchiveHeightAnimation(browser, startHeight, startHeight - archiveHeight, token, opening);
        return;
      }

      const archiveHeight = this.archiveSpanHeight();
      this.runArchiveHeightAnimation(browser, startHeight, startHeight + archiveHeight, token, opening);
    },
    runArchiveHeightAnimation(browser, startHeight, endHeight, token, opening) {
      const duration = opening ? 440 : 380;
      browser.style.height = `${startHeight}px`;
      void browser.offsetHeight;
      this._archiveAnimationFrame = requestAnimationFrame(() => {
        if (this._archiveAnimationToken !== token) return;
        browser.style.transition = `height ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        browser.style.height = `${Math.max(0, endHeight)}px`;
        this._archiveAnimationTimer = window.setTimeout(() => {
          this.finishArchiveAnimation(browser, token, opening);
        }, duration + 24);
      });
    },
    isFeatureEntry(entry) { return Boolean(entry?.showcase); },
    isDetailNavigable(entry) {
      return entry.kind !== 'project' && this.desktopHover && !(this.mode === 'grid' && this.isFeatureEntry(entry)) && this.isEntryOpen(entry.id) && Boolean(collectionDetailRoute(entry));
    },
    openDetail(entry, event) {
      if (!this.isDetailNavigable(entry) || this.transitionId) return;
      const selection = window.getSelection ? window.getSelection() : null;
      if (!shouldNavigateCollectionDetail(event, selection)) return;
      const route = collectionDetailRoute(entry);
      if (event.type === 'keydown') event.preventDefault();
      if (entry.externalPage) {
        window.open(route, '_blank', 'noopener');
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        window.open(this.$router.resolve(route).href, '_blank', 'noopener');
        return;
      }
      this.$router.push(route).catch(() => {});
    },
    prefersReducedMotion() {
      return window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },
    activateTarget(targetId) {
      const entry = this.entries.find((item) => item.id === targetId || item.anchor === targetId);
      if (!entry) return;
      if (this.isArchiveEntry(entry)) {
        this.archiveExpanded = true;
        this.archiveVisible = true;
      }
      // A route target prepares the reading layout before the router lands.
      // Manual expansion has its own viewport hold and view transition, which
      // would fight the cross-page glide if reused here.
      if (this.mode === 'index') {
        if (!this.indexOpenIds.includes(entry.id)) this.indexOpenIds = [...this.indexOpenIds, entry.id];
        this.openId = entry.id;
        this.layoutId = '';
      } else if (!this.isFeatureEntry(entry)) {
        this.openId = entry.id;
        this.layoutId = entry.id;
      }
      this.$emit('target-consumed', targetId);
    },
    startPress(id, event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      window.clearTimeout(this._pressIntentTimer);
      window.clearTimeout(this._pressTimer);
      if (event.currentTarget && event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      this._pressCandidateId = id;
      this._pressIntentTimer = window.setTimeout(() => {
        if (this._pressCandidateId !== id) return;
        this._pressVisibleAt = window.performance ? window.performance.now() : Date.now();
        this.pressedId = id;
      }, 120);
    },
    finishPress(id) {
      window.clearTimeout(this._pressIntentTimer);
      this._pressCandidateId = '';
      if (this.pressedId !== id) return;
      const now = window.performance ? window.performance.now() : Date.now();
      const elapsed = now - (this._pressVisibleAt || now);
      const feedbackFloor = Math.max(0, 120 - elapsed);
      const remaining = Math.max(feedbackFloor, 150 - elapsed);

      this._releasedPressId = id;
      this._activationDelay = remaining + (this.prefersReducedMotion() ? 0 : 180);
      window.clearTimeout(this._pressTimer);
      if (remaining === 0) {
        this.pressedId = '';
        return;
      }
      this._pressTimer = window.setTimeout(() => {
        if (this.pressedId === id) this.pressedId = '';
      }, remaining);
    },
    cancelPress(id) {
      window.clearTimeout(this._pressIntentTimer);
      window.clearTimeout(this._pressTimer);
      this._pressCandidateId = '';
      this._releasedPressId = '';
      this._activationDelay = 0;
      if (this.pressedId === id) this.pressedId = '';
    },
    toggle(id) {
      const isPointerPress = this.pressedId === id || this._releasedPressId === id;
      const activationDelay = isPointerPress ? (this._activationDelay || 180) : 0;
      this._releasedPressId = '';
      this._activationDelay = 0;
      window.clearTimeout(this._activationTimer);
      if (activationDelay > 0) {
        this._activationTimer = window.setTimeout(() => this.commitToggle(id), activationDelay);
        return;
      }
      this.commitToggle(id);
    },
    commitToggle(id) {
      if (this.mode === 'index') {
        const entry = this.entries.find((item) => item.id === id);
        const target = entry && document.getElementById(entry.anchor || entry.id);
        const viewportAnchor = this.consumeTargetViewportAnchor(id) || (target ? {
          kind: 'top',
          entryId: id,
          id: entry.anchor || entry.id,
          top: target.getBoundingClientRect().top,
        } : null);
        this.layoutId = '';
        if (this.indexOpenIds.includes(id)) {
          this.indexOpenIds = this.indexOpenIds.filter((openId) => openId !== id);
          this.openId = this.indexOpenIds[this.indexOpenIds.length - 1] || '';
        } else {
          this.indexOpenIds = [...this.indexOpenIds, id];
          this.openId = id;
        }
        if (viewportAnchor) {
          this.$nextTick(() => this.holdIndexViewportAnchor(viewportAnchor));
        }
        return;
      }
      if (this.openId === id) {
        this.close(id);
        return;
      }
      this.transitionGrid(id);
    },
    close(id = '') {
      if (this.mode === 'index') {
        this.indexOpenIds = id
          ? this.indexOpenIds.filter((openId) => openId !== id)
          : [];
        this.openId = this.indexOpenIds[this.indexOpenIds.length - 1] || '';
        this.layoutId = '';
        return;
      }
      this.transitionGrid('');
    },
    async transitionGrid(nextLayoutId) {
      if (this._gridTransition) {
        const isExistingEndpoint = nextLayoutId === this._gridTransitionFromId
          || nextLayoutId === this._gridTransitionToId;
        if (isExistingEndpoint && nextLayoutId !== this.layoutId) {
          const reversed = await this.reverseGridTransition(nextLayoutId);
          if (reversed) return;
        }
        if (this._gridTransition.skipTransition) this._gridTransition.skipTransition();
      }

      const previousLayoutId = this.layoutId;
      const activeId = nextLayoutId || this.layoutId;
      const viewportAnchor = this.consumeTargetViewportAnchor(activeId)
        || this.captureGridViewportAnchor(activeId);
      const transitionToken = (this._gridTransitionToken || 0) + 1;
      this._gridTransitionToken = transitionToken;
      const reduceMotion = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const cursorStartedAt = window.performance ? window.performance.now() : Date.now();
      const releaseCursorLock = !reduceMotion
        ? acquireInteractiveCursorLock()
        : null;
      if (releaseCursorLock) {
        if (!this._gridCursorReleases) this._gridCursorReleases = new Set();
        this._gridCursorReleases.add(releaseCursorLock);
      }
      this.transitionId = activeId;
      this.transitionAction = nextLayoutId ? 'expand' : 'collapse';
      await this.$nextTick();

      const applyLayout = async () => {
        this.layoutId = nextLayoutId;
        this.openId = nextLayoutId;
        await this.$nextTick();
        this.restoreGridViewportAnchor(viewportAnchor);
      };
      const finishTransition = () => {
        const now = window.performance ? window.performance.now() : Date.now();
        const remaining = reduceMotion
          ? 0
          : Math.max(0, GRID_CURSOR_SETTLE_MS - (now - cursorStartedAt));
        const timer = window.setTimeout(() => {
          if (this._gridCursorTimers) this._gridCursorTimers.delete(timer);
          if (releaseCursorLock) {
            releaseCursorLock();
            if (this._gridCursorReleases) this._gridCursorReleases.delete(releaseCursorLock);
          }
          if (this._gridTransitionToken !== transitionToken) return;
          window.clearTimeout(this._gridReverseTimer);
          this._gridTransition = null;
          this._gridTransitionFromId = '';
          this._gridTransitionToId = '';
          this.transitionId = '';
          this.transitionAction = '';
        }, remaining);
        if (!this._gridCursorTimers) this._gridCursorTimers = new Set();
        this._gridCursorTimers.add(timer);
      };
      if (!document.startViewTransition || reduceMotion) {
        await applyLayout();
        finishTransition();
        return;
      }

      let transition;
      try {
        transition = document.startViewTransition(applyLayout);
      } catch (error) {
        await applyLayout();
        finishTransition();
        return;
      }
      this._gridTransition = transition;
      this._gridTransitionFromId = previousLayoutId;
      this._gridTransitionToId = nextLayoutId;
      transition.finished.catch(() => {}).finally(finishTransition);
    },
    async reverseGridTransition(nextLayoutId) {
      const transition = this._gridTransition;
      if (!transition) return false;

      try {
        await transition.ready;
      } catch (error) {
        return false;
      }

      const animations = document.getAnimations().filter((animation) => {
        const pseudo = animation.effect && animation.effect.pseudoElement;
        return pseudo
          && pseudo.includes('collection-')
          && (animation.playState === 'running' || animation.playState === 'pending');
      });
      if (!animations.length) return false;

      this.layoutId = nextLayoutId;
      this.openId = nextLayoutId;
      this.transitionAction = nextLayoutId ? 'expand' : 'collapse';
      await this.$nextTick();
      animations.forEach((animation) => animation.reverse());

      const finishReverse = () => {
        if (this._gridTransition !== transition) return;
        if (transition.skipTransition) transition.skipTransition();
      };
      window.clearTimeout(this._gridReverseTimer);
      Promise.all(animations.map((animation) => animation.finished.catch(() => {})))
        .then(finishReverse);
      this._gridReverseTimer = window.setTimeout(finishReverse, 360);
      return true;
    },
    captureGridViewportAnchor(entryId) {
      if (!entryId) return null;
      const entry = this.entries.find((item) => item.id === entryId);
      const card = entry && document.getElementById(entry.anchor || entry.id);
      const summary = card && card.querySelector('.collection-card-summary');
      if (!card || !summary) return null;

      const cardRect = card.getBoundingClientRect();
      const summaryRect = summary.getBoundingClientRect();
      const header = document.querySelector('.header');
      const headerRect = header ? header.getBoundingClientRect() : null;
      const headerStyle = header ? window.getComputedStyle(header) : null;
      const headerFloats = headerStyle
        && (headerStyle.position === 'fixed' || headerStyle.position === 'sticky');
      const visibleTop = headerFloats && headerRect && headerRect.bottom > 0
        ? headerRect.bottom
        : 0;
      const clippedAtTop = cardRect.top < visibleTop - 1
        && summaryRect.bottom > visibleTop + 1;
      if (!clippedAtTop) return null;

      const entryIndex = this.entries.findIndex((item) => item.id === entryId);
      return {
        kind: 'summary-bottom',
        id: entry.anchor || entry.id,
        edge: entryIndex % 2 === 0 ? 'left' : 'right',
        summaryBottom: summaryRect.bottom
      };
    },
    consumeTargetViewportAnchor(entryId) {
      const anchor = this._targetViewportAnchor;
      if (!anchor || anchor.entryId !== entryId) return null;
      this._targetViewportAnchor = null;
      return anchor;
    },
    restoreGridViewportAnchor(anchor) {
      if (!anchor) return;
      const card = document.getElementById(anchor.id);
      if (!card) return;
      if (anchor.kind === 'top') {
        const deltaY = card.getBoundingClientRect().top - anchor.top;
        if (Math.abs(deltaY) > 0.5) window.scrollBy(0, deltaY);
        return;
      }
      const summary = card.querySelector('.collection-card-summary');
      if (!summary) return;
      // The selected card grows to the full row. Its left edge therefore stays
      // fixed for a left-column card and its right edge stays fixed for a
      // right-column card. Correct only the vertical scroll position so the
      // matching bottom corner remains at the same viewport height.
      const deltaY = summary.getBoundingClientRect().bottom - anchor.summaryBottom;
      if (Math.abs(deltaY) > 0.5) window.scrollBy(0, deltaY);
    },
    holdIndexViewportAnchor(anchor) {
      if (!anchor) return;
      if (this._indexAnchorFrame) cancelAnimationFrame(this._indexAnchorFrame);
      const startedAt = performance.now();
      const hold = () => {
        this.restoreGridViewportAnchor(anchor);
        if (performance.now() - startedAt < 430) {
          this._indexAnchorFrame = requestAnimationFrame(hold);
        } else {
          this._indexAnchorFrame = null;
        }
      };
      hold();
    }
  }
};
</script>

<style>
.collection-browser {
  --collection-surface: rgba(0, 0, 0, 0.025);
  margin: 2.15rem 0 3.4rem;
  color: #333334;
  font-family: "Quattrocento Sans", sans-serif;
}

.collection-curation-head {
  margin: 0;
  padding: 1rem 0.08rem 0.95rem;
  border-top: 1px solid rgba(60, 60, 67, 0.18);
  border-bottom: 1px solid rgba(60, 60, 67, 0.18);
  color: #747478;
}

.collection-curation-head h2 {
  margin: 0;
  font: 600 0.68rem/1.4 "Quattrocento Sans", sans-serif;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.research-collection-view-stage.is-view-transitioning {
  view-transition-name: research-collection-view;
}

.projects-collection-view-stage.is-view-transitioning {
  view-transition-name: projects-collection-view;
}

::view-transition-group(research-collection-view),
::view-transition-group(projects-collection-view) {
  animation-duration: 320ms;
  animation-timing-function: cubic-bezier(0.32, 0.72, 0, 1);
}

::view-transition-old(research-collection-view),
::view-transition-old(projects-collection-view) {
  animation: collection-view-out 180ms cubic-bezier(0.4, 0, 1, 1) both;
}

::view-transition-new(research-collection-view),
::view-transition-new(projects-collection-view) {
  animation: collection-view-in 320ms cubic-bezier(0.32, 0.72, 0, 1) both;
}

@keyframes collection-view-out {
  to {
    opacity: 0;
    transform: translateY(-4px) scale(0.995);
  }
}

@keyframes collection-view-in {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.995);
  }
}

.collection-grid {
  display: grid;
  gap: 0;
}

.collection-grid-row {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0;
  border-top: 1px solid rgba(60, 60, 67, 0.15);
}

.collection-curation-head + .collection-grid .collection-grid-row {
  border-top: 0;
}

.collection-card {
  position: relative;
  display: block;
  box-sizing: border-box;
  flex: 0 0 50%;
  min-width: 0;
  overflow: hidden;
  padding: 0;
  border: 0;
  border-bottom: 1px solid rgba(60, 60, 67, 0.15);
  border-radius: 0;
  background: transparent;
  color: inherit;
  cursor: var(--site-interactive-cursor);
  font: inherit;
  text-align: left;
  transition: background-color 180ms ease, opacity 220ms cubic-bezier(0.23, 1, 0.32, 1), transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-card::after {
  position: absolute;
  z-index: 3;
  top: 1rem;
  bottom: 1rem;
  left: 0.36rem;
  width: 1px;
  background: transparent;
  content: '';
  pointer-events: none;
  /* Fade the rule in place so its one-pixel edge stays on the same raster. */
  transition: background-color 180ms ease;
}

.collection-archive-card-enter-active {
  transition: opacity 260ms cubic-bezier(0.23, 1, 0.32, 1), transform 280ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-archive-card-leave-active {
  transition: opacity 210ms cubic-bezier(0.23, 1, 0.32, 1), transform 230ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-archive-card-enter-from,
.collection-archive-card-leave-to {
  opacity: 0;
  transform: translate3d(0, -5px, 0);
}

.collection-card.is-archive-entry {
  order: 2;
  background: transparent;
}

.collection-card.is-archive-entry:not(.selected) .collection-card-summary {
  min-height: 11.5rem;
}

.collection-card.is-archive-entry .collection-card-summary strong {
  font-size: 1.16rem;
}

.collection-card.is-archive-entry .collection-card-preview {
  font-size: 0.8rem;
  line-height: 1.45;
}

.collection-card.selected {
  flex-basis: 100%;
}

.collection-card.is-signature {
  flex-basis: 100%;
  overflow: visible;
  cursor: var(--site-default-cursor);
}

.collection-archive-gateway {
  order: 1;
  box-sizing: border-box;
  flex: 0 0 100%;
  margin: 0;
  border-top: 0;
  border-bottom: 1px solid rgba(60, 60, 67, 0.12);
}

.collection-archive-toggle {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 0.08rem 0.95rem;
  border: 0;
  background: transparent;
  color: #747478;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transform: translate3d(0, 0, 0) scale(1);
  transition: background-color 180ms ease, color 160ms ease, transform 130ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-archive-copy {
  display: flex;
  align-items: baseline;
  gap: 0.7rem;
}

.collection-archive-copy strong {
  display: inline-block;
  font: 600 0.68rem/1.4 "Quattrocento Sans", sans-serif;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  transform: translate3d(0, 0, 0);
  transition: letter-spacing 220ms ease, transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-archive-copy span {
  color: #88888c;
  font-size: 0.66rem;
  letter-spacing: 0.075em;
  text-transform: uppercase;
}

.collection-archive-glyph {
  display: inline-block;
  color: #7f7f83;
  font-size: 0.9rem;
  line-height: 1;
  transform: rotate(0deg);
  transition: color 160ms ease, transform 240ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-archive-glyph.is-open {
  transform: rotate(180deg);
}

.collection-archive-toggle:active {
  transform: translate3d(0, 1px, 0) scale(0.995);
}

.collection-transition-hit-area {
  position: absolute;
  z-index: 4;
  inset: 0;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: inherit;
}

.collection-card-summary {
  display: flex;
  width: 100%;
  min-height: 15.5rem;
  box-sizing: border-box;
  flex-direction: column;
  align-items: flex-start;
  padding: 1.05rem 1.08rem 0.95rem;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  scale: 1;
  touch-action: manipulation;
  transition: min-height 260ms cubic-bezier(0.23, 1, 0.32, 1), transform 130ms cubic-bezier(0.23, 1, 0.32, 1);
}

/* Flex lines equalise their card boxes. Let every closed card's button fill
   that shared row height so the whole visible surface remains interactive. */
.collection-card:not(.selected) .collection-card-summary {
  height: 100%;
}

.collection-card.selected .collection-card-summary { min-height: 0; }

.collection-card-meta {
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 0.8rem;
  margin-bottom: 1.35rem;
  color: #88888c;
  font-size: 0.66rem;
  letter-spacing: 0.075em;
  line-height: 1.25;
  text-transform: uppercase;
  transform: translate3d(0, 0, 0);
  transition: color 180ms ease, transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-card-meta span:last-child {
  text-align: right;
  white-space: nowrap;
}

.collection-card-summary strong {
  display: block;
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: 1.3rem;
  font-weight: 600;
  letter-spacing: -0.015em;
  line-height: 1.08;
  text-wrap: balance;
  transition: color 180ms ease;
}

.collection-card-subtitle {
  margin-top: 0.4rem;
  color: #77777a;
  font-size: 0.77rem;
  line-height: 1.35;
}

.collection-card-preview {
  margin-top: 0.9rem;
  color: #5e5e61;
  font-family: Georgia, serif;
  font-size: 0.84rem;
  line-height: 1.48;
  transform: translate3d(0, 0, 0);
  transition: color 180ms ease, transform 240ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-card-cue {
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 1rem;
  color: #77777a;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  transition: color 180ms ease;
}

.collection-card-cue > span {
  display: inline-block;
  transform: translate3d(0, 0, 0);
  transition: color 180ms ease, transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-card.selected {
  background: transparent;
}

.collection-card-summary.is-pressed,
.collection-index-row.is-pressed {
  scale: 1;
  transform: translate3d(0, 2px, 0) scale(0.995);
  transition-duration: 70ms;
  transition-timing-function: cubic-bezier(0.32, 0, 0.67, 0);
}
.collection-card-summary:focus-visible,
.collection-index-row:focus-visible,
.collection-archive-toggle:focus-visible,
.collection-detail-close:focus-visible,
.collection-project-link:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 3px;
}

@media (hover: hover) and (pointer: fine) {
  .collection-card:has(> .collection-card-summary:hover) {
    background: var(--collection-surface, rgba(0, 0, 0, 0.025));
  }

  .collection-card:has(> .collection-card-summary:hover)::after {
    background: color-mix(in srgb, currentColor 16%, transparent);
  }

  .collection-card:has(> .collection-card-summary:hover) .collection-card-meta {
    transform: translate3d(0, 1px, 0);
  }

  .collection-card:has(> .collection-card-summary:hover) .collection-card-preview {
    transform: translate3d(0, -2px, 0);
  }

  .collection-card:has(> .collection-card-summary:hover) .collection-card-cue > span {
    transform: translate3d(-3px, 0, 0);
  }

  .collection-archive-toggle:hover {
    background: rgba(0, 0, 0, 0.025);
    color: #000;
  }

  .collection-archive-toggle:hover .collection-archive-copy strong {
    transform: translate3d(0.12rem, 0, 0);
  }

  .collection-archive-toggle:hover .collection-archive-glyph {
    color: #353537;
    transform: translate3d(0, 0.18rem, 0) rotate(0deg);
  }

  .collection-archive-toggle:hover .collection-archive-glyph.is-open {
    transform: translate3d(0, -0.18rem, 0) rotate(180deg);
  }

  .collection-detail-close:hover {
    background: rgba(118, 118, 128, 0.17);
    color: #222224;
  }

}

.collection-detail-shell {
  /* Reading regions must not inherit the outer toggle seam's hand cursor. */
  cursor: var(--site-default-cursor);
  display: grid;
  grid-template-rows: 0fr;
  visibility: hidden;
  transition: grid-template-rows 280ms cubic-bezier(0.32, 0.72, 0, 1), visibility 0s linear 280ms;
}

.collection-detail-shell.open {
  grid-template-rows: 1fr;
  visibility: visible;
  transition: grid-template-rows 380ms cubic-bezier(0.32, 0.72, 0, 1), visibility 0s;
}

.collection-detail-clip {
  min-height: 0;
  overflow: hidden;
  transform: translate3d(0, -0.7rem, 0);
  transition: transform 280ms cubic-bezier(0.32, 0.72, 0, 1);
}

.collection-detail-shell.open .collection-detail-clip {
  transform: translate3d(0, 0, 0);
  transition-duration: 380ms;
}

.collection-browser.is-grid .collection-detail-shell.open {
  transition-duration: 440ms;
}

.collection-browser.is-grid .collection-detail-shell.open .collection-detail-clip {
  transition-duration: 460ms;
}

.collection-browser.is-grid.is-layout-transitioning .collection-detail-shell,
.collection-browser.is-grid.is-layout-transitioning .collection-detail-clip {
  transition: none;
}

.collection-detail {
  margin-top: 0.9rem;
  padding: 1.25rem 1.35rem 1.3rem;
  border-top: 1px solid rgba(60, 60, 67, 0.18);
  border-bottom: 1px solid rgba(60, 60, 67, 0.12);
  background: rgba(0, 0, 0, 0.018);
}

.collection-detail-embedded {
  margin-top: 0;
  padding-top: 0.15rem;
  border: 0;
  background: transparent;
}

.collection-detail-navigable {
  cursor: var(--site-interactive-cursor);
}

.collection-detail-navigable:focus-visible {
  outline: 2px solid var(--site-accent);
  outline-offset: -2px;
}

.collection-detail-embedded-subtitle {
  margin: 0.2rem 0 0;
  color: #77777a;
  font-size: 0.92rem;
}

.collection-detail-lab {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.65rem;
  margin-top: 1rem;
  color: #6f6f72;
  font-size: 0.88rem;
}

.collection-detail-logo {
  display: inline-flex;
  flex: 0 0 auto;
  padding: 5px;
  border-radius: 5px;
  background: #fff;
}

.collection-detail-logo img {
  display: block;
  width: auto;
  max-width: 9rem;
  object-fit: contain;
}

div.scroll-container.project.proof-gallery.collection-detail-gallery {
  --proof-gallery-height: clamp(210px, 34vh, 300px);
  height: auto;
  align-items: center;
  margin-top: 1.1rem;
  padding-top: 0;
  padding-bottom: 0;
  border-radius: 0;
  background: transparent;
}

div.scroll-container.project.proof-gallery.collection-detail-gallery img {
  width: auto;
  height: auto;
  max-height: calc(var(--proof-gallery-height) - 20px);
  padding: 0;
  border: 0;
  border-radius: 0 !important;
  background: transparent;
  box-shadow: none;
}

div.scroll-container.project.proof-gallery.collection-detail-gallery.proof-gallery-lg {
  --proof-gallery-height: clamp(270px, 42vh, 380px);
}

div.scroll-container.project.proof-gallery.collection-detail-gallery.proof-gallery-rounded,
div.scroll-container.project.proof-gallery.collection-detail-gallery.proof-gallery-rounded img {
  border-radius: 0 !important;
}

div.scroll-container.project.proof-gallery.collection-detail-gallery.collection-detail-gallery-compact {
  --proof-gallery-height: 20vh;
}

div.scroll-container.project.proof-gallery.collection-detail-gallery.collection-detail-hero {
  display: block;
  overflow: visible;
  white-space: normal;
}

div.scroll-container.project.proof-gallery.collection-detail-gallery.collection-detail-hero img {
  display: block;
  width: 100%;
  max-width: none;
  max-height: none;
}

div.scroll-container.project.proof-gallery.collection-detail-gallery.collection-detail-hero-half img {
  width: 50%;
}

.collection-detail-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.collection-detail-kicker {
  color: #88888c;
  font-size: 0.68rem;
  letter-spacing: 0.075em;
  text-transform: uppercase;
}

.collection-detail h3 {
  margin: 0.35rem 0 0;
  font-size: 1.45rem;
  line-height: 1.22;
}

.collection-detail-subtitle {
  margin: 0.2rem 0 0;
  color: #77777a;
  font-size: 0.92rem;
}

.collection-detail-close {
  flex: 0 0 auto;
  width: 1.9rem;
  height: 1.9rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(118, 118, 128, 0.1);
  color: #666668;
  font: 300 1.15rem/1 "Quattrocento Sans", sans-serif;
  cursor: pointer;
  transition: background-color 140ms ease, transform 120ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-detail-close:active { transform: scale(0.9); }

.collection-detail-body {
  margin-top: 1rem;
  color: #505054;
  font-family: Georgia, serif;
  font-size: 0.96rem;
  line-height: 1.7;
}

.collection-detail-body p { margin: 0.85rem 0 0; }

.collection-detail-footer {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.15rem;
  margin-top: 1.05rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(60, 60, 67, 0.1);
}

.collection-detail-status {
  margin-right: auto;
  color: #77777a;
  font-size: 0.7rem;
  letter-spacing: 0.075em;
  text-transform: uppercase;
}

.collection-project-link,
.collection-project-link:visited {
  position: relative;
  display: inline-flex;
  align-items: baseline;
  align-self: flex-start;
  gap: 0.42rem;
  padding: 0.12rem 0 0.2rem;
  color: #4f4d49;
  font-size: 0.88rem;
  font-weight: 500;
  text-decoration: none;
  transition: color 160ms ease;
}

.collection-project-link::after {
  position: absolute;
  right: 1.3rem;
  bottom: 0.08rem;
  left: 0;
  height: 1px;
  background: currentColor;
  content: '';
  opacity: 0;
  transform: scaleX(0);
  transform-origin: left center;
  transition: opacity 160ms ease, transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-project-arrow {
  flex: 0 0 auto;
  opacity: 0.68;
  transition: opacity 160ms ease, transform 200ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-index {
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(60, 60, 67, 0.18);
}

.collection-index-unit {
  border-bottom: 1px solid rgba(60, 60, 67, 0.14);
  cursor: var(--site-interactive-cursor);
  transition: background-color 180ms ease, opacity 220ms cubic-bezier(0.23, 1, 0.32, 1), transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-index-unit.is-archive-entry {
  order: 2;
}

.collection-index .collection-archive-gateway {
  margin-top: 0;
}

.collection-curation-head + .collection-index {
  border-top: 0;
}

.collection-card,
.collection-index-unit {
  scroll-margin-top: 20px;
}

.collection-index-row {
  position: relative;
  display: grid;
  width: 100%;
  grid-template-columns: 6.7rem minmax(0, 1fr) 12.5rem 1rem;
  align-items: baseline;
  gap: 0.8rem;
  padding: 0.78rem 0.2rem;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  scale: 1;
  touch-action: manipulation;
  transform: translate3d(0, 0, 0) scale(1);
  transition: color 150ms ease, background-color 150ms ease, transform 180ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-index-row::before {
  position: absolute;
  z-index: 0;
  right: 0.2rem;
  bottom: 0;
  left: 0.2rem;
  height: 1px;
  background: currentColor;
  content: '';
  opacity: 0;
  pointer-events: none;
  transform: scaleX(0.72);
  transform-origin: left center;
  transition: opacity 170ms ease, transform 240ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-index-row > * {
  position: relative;
  z-index: 1;
}

.collection-index-category,
.collection-index-period {
  color: #88888c;
  font-size: 0.66rem;
  letter-spacing: 0.065em;
  text-transform: uppercase;
  transform: translate3d(0, 0, 0);
  transition: color 170ms ease, transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-index-row strong {
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: 1.04rem;
  font-weight: 600;
  line-height: 1.2;
  transition: color 170ms ease;
}

.collection-index-period { text-align: right; }
.collection-index-period { white-space: nowrap; }
.collection-index-cue {
  color: #88888c;
  text-align: center;
  transform: translate3d(0, 0, 0);
  transition: color 170ms ease, transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
}

.collection-index-row.selected { color: #000; }

@media (hover: hover) and (pointer: fine) {
  .collection-card.is-detail-navigable:hover {
    background: var(--collection-surface, rgba(0, 0, 0, 0.025));
  }

  .collection-index-unit.is-detail-navigable:hover {
    background: var(--collection-surface, rgba(0, 0, 0, 0.025));
  }

  .collection-index-row:hover {
    background: var(--collection-surface, rgba(0, 0, 0, 0.025));
    color: #000;
  }

  .collection-index-row:hover::before {
    opacity: 0.12;
    transform: scaleX(1);
  }

  .collection-index-row:hover .collection-index-category {
    transform: translate3d(2px, 0, 0);
  }

  .collection-index-row:hover .collection-index-period,
  .collection-index-row:hover .collection-index-cue {
    transform: translate3d(-2px, 0, 0);
  }

  .collection-index-unit.is-detail-navigable:hover .collection-index-row {
    background: transparent;
  }

  .collection-detail-navigable:hover .collection-project-link,
  .collection-detail-navigable:focus-visible .collection-project-link,
  .collection-project-link:hover,
  .collection-project-link:focus-visible {
    color: #191817;
  }

  .collection-detail-navigable:hover .collection-project-link::after,
  .collection-detail-navigable:focus-visible .collection-project-link::after,
  .collection-project-link:hover::after,
  .collection-project-link:focus-visible::after {
    opacity: 0.58;
    transform: scaleX(1);
  }

  .collection-detail-navigable:hover .collection-project-arrow,
  .collection-detail-navigable:focus-visible .collection-project-arrow,
  .collection-project-link:hover .collection-project-arrow,
  .collection-project-link:focus-visible .collection-project-arrow {
    opacity: 1;
    transform: translateX(3px);
  }

  /* Other controls keep the emphasis on their own action. The toggle
     header sits outside this region and continues to signal collapse. */
  .collection-detail-navigable:has(a:hover, button:hover):not(:has(.collection-project-link:hover)) .collection-project-link {
    color: #4f4d49;
  }

  .collection-detail-navigable:has(a:hover, button:hover):not(:has(.collection-project-link:hover)) .collection-project-link::after {
    opacity: 0;
    transform: scaleX(0);
  }

  .collection-detail-navigable:has(a:hover, button:hover):not(:has(.collection-project-link:hover)) .collection-project-arrow {
    opacity: 0.68;
    transform: none;
  }
}

body.dark-mode .collection-browser {
  --collection-surface: rgba(255, 255, 255, 0.055);
  color: #d7d7d9;
}
body.dark-mode .collection-curation-head,
body.dark-mode .collection-curation-head h2 { color: #a5a5aa; }
body.dark-mode .collection-archive-copy span,
body.dark-mode .collection-archive-glyph { color: var(--site-text-tertiary); }
body.dark-mode .collection-curation-head,
body.dark-mode .collection-archive-gateway,
body.dark-mode .collection-grid-row,
body.dark-mode .collection-card { border-color: rgba(255, 255, 255, 0.13); }
body.dark-mode .collection-archive-toggle { color: #a5a5aa; }
body.dark-mode .collection-card {
  background: transparent;
}
body.dark-mode .collection-card.is-archive-entry { background: transparent; }
body.dark-mode .collection-card.selected { background: transparent; }
body.dark-mode .collection-card-meta,
body.dark-mode .collection-card-subtitle,
body.dark-mode .collection-card-cue,
body.dark-mode .collection-detail-kicker,
body.dark-mode .collection-detail-subtitle,
body.dark-mode .collection-detail-status,
body.dark-mode .collection-index-category,
body.dark-mode .collection-index-period,
body.dark-mode .collection-index-cue { color: var(--site-text-tertiary); }
body.dark-mode .collection-card-preview,
body.dark-mode .collection-detail-body { color: var(--site-text-secondary); }
body.dark-mode .collection-detail {
  border-color: rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.025);
}
body.dark-mode .collection-detail.collection-detail-embedded {
  background: transparent;
}
body.dark-mode .collection-detail-lab { color: var(--site-text-secondary); }
body.dark-mode .collection-detail-footer,
body.dark-mode .collection-index,
body.dark-mode .collection-index-unit { border-color: rgba(255, 255, 255, 0.13); }
body.dark-mode .collection-detail-close { background: rgba(255, 255, 255, 0.09); color: #d1d1d4; }
body.dark-mode .collection-project-link,
body.dark-mode .collection-project-link:visited { color: #d6d1c9; }
body.dark-mode .collection-index-row.selected { color: #fff; }
@media (hover: hover) and (pointer: fine) {
  body.dark-mode .collection-card:has(> .collection-card-summary:hover) {
    background: var(--collection-surface, rgba(255, 255, 255, 0.055));
  }

  body.dark-mode .collection-archive-toggle:hover {
    background: rgba(255, 255, 255, 0.055);
    color: #fff;
  }

  body.dark-mode .collection-archive-toggle:hover .collection-archive-glyph {
    color: #d7d7d9;
  }

  body.dark-mode .collection-index-row:hover {
    background: var(--collection-surface, rgba(255, 255, 255, 0.055));
    color: #fff;
  }

  body.dark-mode .collection-card.is-detail-navigable:hover,
  body.dark-mode .collection-index-unit.is-detail-navigable:hover {
    background: var(--collection-surface, rgba(255, 255, 255, 0.055));
  }

  body.dark-mode .collection-index-unit.is-detail-navigable:hover .collection-index-row {
    background: transparent;
  }

  body.dark-mode .collection-detail-close:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
  }

  body.dark-mode .collection-detail-navigable:hover .collection-project-link,
  body.dark-mode .collection-detail-navigable:focus-visible .collection-project-link,
  body.dark-mode .collection-project-link:hover,
  body.dark-mode .collection-project-link:focus-visible { color: #f4f1ec; }

  body.dark-mode .collection-detail-navigable:has(a:hover, button:hover):not(:has(.collection-project-link:hover)) .collection-project-link {
    color: #cfcbc5;
  }
}

@media (max-width: 660px) {
  .collection-card { flex-basis: 100%; }
  .collection-card-summary { min-height: 0; }
  .collection-index-row {
    grid-template-columns: minmax(0, 1fr) auto 1rem;
    gap: 0.45rem 0.75rem;
  }
  .collection-index-category { grid-column: 1; grid-row: 1; }
  .collection-index-row strong { grid-column: 1 / -1; grid-row: 2; }
  .collection-index-period { grid-column: 2; grid-row: 1; }
  .collection-index-cue { grid-column: 3; grid-row: 1; }
  .collection-detail { padding-right: 0.9rem; padding-left: 0.9rem; }
  .collection-curation-head { padding-top: 0.9rem; padding-bottom: 0.85rem; }
  .collection-archive-toggle { padding-top: 0.9rem; padding-bottom: 0.85rem; }
}

@media (max-width: 1100px) {
  .collection-card,
  .collection-index-unit {
    scroll-margin-top: 64px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .collection-card,
  .collection-card-summary,
  .collection-detail-shell,
  .collection-detail-shell.open,
  .collection-detail-close,
  .collection-index-unit,
  .collection-index-row,
  .collection-archive-toggle {
    transition-duration: 80ms;
  }

  .collection-archive-card-enter-from,
  .collection-archive-card-leave-to {
    transform: none;
  }

  .collection-archive-card-enter-active,
  .collection-archive-card-leave-active {
    transition-duration: 120ms;
  }

  .collection-card::after,
  .collection-card-meta,
  .collection-card-preview,
  .collection-card-cue > span,
  .collection-index-row::before,
  .collection-index-category,
  .collection-index-period,
  .collection-index-cue {
    transition-duration: 80ms;
  }

  .collection-card:has(> .collection-card-summary:hover)::after,
  .collection-card:has(> .collection-card-summary:hover) .collection-card-meta,
  .collection-card:has(> .collection-card-summary:hover) .collection-card-preview,
  .collection-card:has(> .collection-card-summary:hover) .collection-card-cue > span,
  .collection-index-row:hover::before,
  .collection-index-row:hover .collection-index-category,
  .collection-index-row:hover .collection-index-period,
  .collection-index-row:hover .collection-index-cue {
    transform: none;
  }

}

::view-transition-group(*) {
  animation-duration: 280ms;
  animation-timing-function: cubic-bezier(0.77, 0, 0.175, 1);
}

::view-transition-group(collection-active-card) {
  animation-duration: 280ms;
  animation-timing-function: cubic-bezier(0.77, 0, 0.175, 1);
}

::view-transition {
  pointer-events: none;
}

html:has(.collection-view-stage.is-view-transitioning)::view-transition,
html:has(.collection-browser.is-layout-transitioning)::view-transition,
html:has(.fragments-page.is-category-transitioning)::view-transition {
  cursor: var(--site-interactive-cursor);
}

html:has(.collection-browser.is-layout-transitioning) .collection-card,
html:has(.collection-browser.is-layout-transitioning) .collection-card * {
  cursor: var(--site-interactive-cursor);
}

::view-transition-group(root),
::view-transition-old(root),
::view-transition-new(root) {
  animation: none;
}

/* Native cursors track the pointer directly. Only the arrow artwork moves,
   with the same centre hotspot in every frame. */
@media (hover: hover) and (pointer: fine) {
  html.interactive-cursor-lock:has(.collection-browser.is-transition-expand) {
    --collection-transition-cursor: var(--collection-expand-cursor-start);
  }

  html.interactive-cursor-lock:has(.collection-browser.is-transition-collapse) {
    --collection-transition-cursor: var(--collection-collapse-cursor-start);
  }

  html.interactive-cursor-lock:has(.collection-browser.is-transition-expand),
  html.interactive-cursor-lock:has(.collection-browser.is-transition-collapse),
  html.interactive-cursor-lock:has(.collection-browser.is-transition-expand) body,
  html.interactive-cursor-lock:has(.collection-browser.is-transition-expand) body *,
  html.interactive-cursor-lock:has(.collection-browser.is-transition-collapse) body,
  html.interactive-cursor-lock:has(.collection-browser.is-transition-collapse) body *,
  html.interactive-cursor-lock:has(.collection-browser.is-transition-expand)::view-transition,
  html.interactive-cursor-lock:has(.collection-browser.is-transition-expand)::view-transition-group(*),
  html.interactive-cursor-lock:has(.collection-browser.is-transition-expand)::view-transition-image-pair(*),
  html.interactive-cursor-lock:has(.collection-browser.is-transition-expand)::view-transition-old(*),
  html.interactive-cursor-lock:has(.collection-browser.is-transition-expand)::view-transition-new(*),
  html.interactive-cursor-lock:has(.collection-browser.is-transition-collapse)::view-transition,
  html.interactive-cursor-lock:has(.collection-browser.is-transition-collapse)::view-transition-group(*),
  html.interactive-cursor-lock:has(.collection-browser.is-transition-collapse)::view-transition-image-pair(*),
  html.interactive-cursor-lock:has(.collection-browser.is-transition-collapse)::view-transition-old(*),
  html.interactive-cursor-lock:has(.collection-browser.is-transition-collapse)::view-transition-new(*) {
    cursor: var(--collection-transition-cursor) !important;
  }

  #app .collection-browser [data-collection-toggle="expand"] {
    --collection-toggle-cursor: var(--collection-expand-cursor-start);
    --collection-cursor-animation: collection-cursor-expand;
  }

  #app .collection-browser [data-collection-toggle="collapse"] {
    --collection-toggle-cursor: var(--collection-collapse-cursor-start);
    --collection-cursor-animation: collection-cursor-collapse;
  }

  #app .collection-browser [data-collection-toggle],
  #app .collection-browser [data-collection-toggle] * {
    cursor: var(--collection-toggle-cursor, var(--site-interactive-cursor)) !important;
  }

  #app .collection-browser [data-collection-toggle]:hover {
    animation: var(--collection-cursor-animation) 1600ms steps(1, end) -320ms infinite;
  }

  html.home-glass-preview #app .collection-browser [data-collection-toggle]:hover {
    animation: none;
  }

  /* Keep the same action hint while the grid's transition layer owns hit
     testing. The existing transition animations remain on their groups. */
  html.interactive-cursor-lock:has([data-collection-toggle="expand"]:hover)::view-transition {
    --collection-toggle-cursor: var(--collection-expand-cursor-start);
    --collection-cursor-animation: collection-cursor-expand;
    animation: var(--collection-cursor-animation) 1600ms steps(1, end) -320ms infinite;
  }

  html.interactive-cursor-lock:has([data-collection-toggle="collapse"]:hover)::view-transition {
    --collection-toggle-cursor: var(--collection-collapse-cursor-start);
    --collection-cursor-animation: collection-cursor-collapse;
    animation: var(--collection-cursor-animation) 1600ms steps(1, end) -320ms infinite;
  }

  html.interactive-cursor-lock:has([data-collection-toggle]:hover)::view-transition,
  html.interactive-cursor-lock:has([data-collection-toggle]:hover)::view-transition-group(*),
  html.interactive-cursor-lock:has([data-collection-toggle]:hover)::view-transition-image-pair(*),
  html.interactive-cursor-lock:has([data-collection-toggle]:hover)::view-transition-old(*),
  html.interactive-cursor-lock:has([data-collection-toggle]:hover)::view-transition-new(*) {
    cursor: var(--collection-transition-cursor, var(--collection-toggle-cursor, var(--site-interactive-cursor))) !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  #app .collection-browser [data-collection-toggle]:hover,
  html.interactive-cursor-lock:has([data-collection-toggle]:hover)::view-transition {
    animation: none;
  }
}
</style>
